import { Box, Carousel, CarouselSlide, FluidImage, FlexBox, Dropdown, DropdownOption, Button, BasicPreloader } from '@vp/swan';
import classnames from 'classnames';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import axios from 'axios';
import { S as Side, g as getProductPadding, a as getVortexProductConfigurationFromSKU, e as ensureProjectionsIgnored, i as isSKUSupported } from './vortexProductConfiguration.js';
export { S as productSide } from './vortexProductConfiguration.js';
import queryString from 'query-string';

const PreviewModes = {
  FirstPageOnly: "firstPageOnly",
  // Show the front page only with a static preview
  Carousel: "carousel",
  // Multi-page view of product with static previews of each page
  SpecificPageOnly: "specificPageOnly",
  // Show the named page only with a static preview
  Vortex: "vortex" // Show a dynamic vortex preview

};

function getMaxWidthForMaxHeight(maxHeight, imageWidth, imageHeight) {
  const imageAspectRatio = imageWidth / imageHeight;
  return imageHeight > maxHeight ? maxHeight * imageAspectRatio : imageWidth;
}

function CarouselPreview({
  purcsPreviews,
  onClick,
  onImageLoadError,
  pageSelectorOptions,
  fullSizeButtonOptions,
  maxHeight,
  purcsImageDimensions,
  accessibleTextForNext,
  accessibleTextForPrevious,
  afterChange,
  addOn
}) {
  const [activeSlide, setActiveSlide] = useState(0);
  const previewsCarousel = useRef();
  const multiplePreviews = (purcsPreviews == null ? void 0 : purcsPreviews.length) > 1;

  const handleBeforeChange = (current, next) => {
    setActiveSlide(next);
  };

  const handlePageChange = event => {
    // eslint-disable-next-line radix
    const index = parseInt(event.target.value);
    previewsCarousel.current.slickGoTo(index);
  };

  const handleFullSizeButtonOnClick = () => {
    // find current purcs preview and return it in callback
    if (purcsPreviews && purcsPreviews.length > 0) {
      const preview = purcsPreviews[activeSlide];

      if (fullSizeButtonOptions && fullSizeButtonOptions.onClick) {
        fullSizeButtonOptions.onClick(preview);
      }
    }
  };

  const handleAfterChange = active => {
    afterChange && afterChange(purcsPreviews[active]);
  };

  const addOnElement = useMemo(() => {
    if (addOn === undefined || addOn === null) return /*#__PURE__*/React.createElement(React.Fragment, null);

    if (typeof addOn === "function") {
      return addOn();
    }

    return addOn;
  }, [addOn]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    bgc: purcsPreviews.length <= 0 ? "loading-shimmer" : undefined,
    className: classnames("purcs-preview")
  }, /*#__PURE__*/React.createElement(Carousel, {
    skin: "standard",
    slidesToShow: 1,
    infinite: false,
    ref: previewsCarousel,
    beforeChange: handleBeforeChange,
    accessibleTextForNext: accessibleTextForNext,
    accessibleTextForPrevious: accessibleTextForPrevious,
    afterChange: handleAfterChange
  }, purcsPreviews.map((preview, index) => {
    let maxWidth;

    if (maxHeight && purcsImageDimensions && purcsImageDimensions[index]) {
      maxWidth = getMaxWidthForMaxHeight(maxHeight, purcsImageDimensions[index].width, purcsImageDimensions[index].height);
    }

    return onClick ? /*#__PURE__*/React.createElement(CarouselSlide, {
      key: `${preview.title} preview`
    }, /*#__PURE__*/React.createElement("div", {
      key: preview.src,
      onClick: onClick,
      style: {
        pointerEvents: index !== activeSlide ? "none" : "all"
      }
    }, addOnElement, /*#__PURE__*/React.createElement(FluidImage, {
      className: "purcs-preview-img",
      src: preview.src,
      key: preview.src,
      srcSet: preview.srcSet,
      alt: preview.title,
      onError: onImageLoadError,
      style: {
        maxWidth
      },
      marginX: maxHeight ? "auto" : ""
    }))) : /*#__PURE__*/React.createElement(CarouselSlide, null, addOnElement, /*#__PURE__*/React.createElement(FluidImage, {
      className: "purcs-preview-img",
      src: preview.src,
      key: preview.src,
      srcSet: preview.srcSet,
      alt: preview.title,
      onError: onImageLoadError,
      width: maxWidth,
      style: {
        maxWidth
      },
      marginX: maxHeight ? "auto" : ""
    }));
  }))), purcsPreviews && purcsPreviews.length > 0 && /*#__PURE__*/React.createElement(FlexBox, {
    className: "pages-section",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 6,
    height: 50
  }, pageSelectorOptions && pageSelectorOptions.show === true && multiplePreviews && /*#__PURE__*/React.createElement(Dropdown, {
    skin: "standard",
    onChange: handlePageChange,
    value: activeSlide
  }, purcsPreviews.map((preview, index) => {
    return /*#__PURE__*/React.createElement(DropdownOption, {
      value: index,
      key: index
    }, pageSelectorOptions.pageOptionText ? pageSelectorOptions.pageOptionText.replace("{{page}}", index + 1) : index + 1);
  })), fullSizeButtonOptions && fullSizeButtonOptions.show === true && /*#__PURE__*/React.createElement(Button, {
    skin: "secondary",
    size: "standard",
    width: "standard",
    marginLeft: 2,
    onClick: handleFullSizeButtonOnClick
  }, fullSizeButtonOptions.text)));
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null
    };
  }

  componentDidCatch(error, errorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({
      error,
      errorInfo
    }); // You can also log error messages to an error reporting service here
  }

  render() {
    if (this.state.error) {
      // Rendering fallback UI and tracking it
      this.props.fallbackPreviewTracking("Vortex preview fallback", this.state.error);
      return /*#__PURE__*/React.createElement("div", null);
    }

    return this.props.children;
  }

}

const defaultPurcsPreviewOptions = Object.freeze({
  workEntityServiceOrigin: "https://workentityservice.design.vpsvc.com",
  requestor: "purcs-client",
  tenantType: "merchants",
  tenantId: "vistaprint-prod",
  vistaprintTenant: "vistaprint",
  promotiqueTenant: "promotique",
  locale: "en-IE",
  purpose: "design.studio.review",
  width: 1000
});

const img = "data:image/svg+xml,%3csvg width='152' height='152' viewBox='0 0 152 152' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cg filter='url(%23filter0_i)'%3e%3cpath d='M71.6699 7.50001C73.5944 4.16667 78.4056 4.16667 80.3301 7.5L137.488 106.5C139.412 109.833 137.007 114 133.158 114H18.8423C14.9933 114 12.5877 109.833 14.5122 106.5L71.6699 7.50001Z' fill='url(%23paint0_linear)'/%3e%3c/g%3e%3cpath d='M79.8971 7.75L137.055 106.75C138.787 109.75 136.622 113.5 133.158 113.5H18.8423C15.3782 113.5 13.2132 109.75 14.9452 106.75L72.1029 7.75001C73.8349 4.75 78.1651 4.75 79.8971 7.75Z' stroke='black'/%3e%3cpath d='M79.3906 82.7344H71.5625L70.9531 33.75H80.0469L79.3906 82.7344ZM70.625 97.6406C70.625 96.2344 71.0469 95.0625 71.8906 94.125C72.7656 93.1562 74.0469 92.6719 75.7344 92.6719C77.4219 92.6719 78.7031 93.1562 79.5781 94.125C80.4531 95.0625 80.8906 96.2344 80.8906 97.6406C80.8906 99.0469 80.4531 100.219 79.5781 101.156C78.7031 102.062 77.4219 102.516 75.7344 102.516C74.0469 102.516 72.7656 102.062 71.8906 101.156C71.0469 100.219 70.625 99.0469 70.625 97.6406Z' fill='black'/%3e%3cdefs%3e%3cfilter id='filter0_i' x='13.8349' y='2' width='128.33' height='112' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3e%3cfeFlood flood-opacity='0' result='BackgroundImageFix'/%3e%3cfeBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape'/%3e%3cfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3e%3cfeOffset dx='4' dy='-3'/%3e%3cfeGaussianBlur stdDeviation='2'/%3e%3cfeComposite in2='hardAlpha' operator='arithmetic' k2='-1' k3='1'/%3e%3cfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'/%3e%3cfeBlend mode='normal' in2='shape' result='effect1_innerShadow'/%3e%3c/filter%3e%3clinearGradient id='paint0_linear' x1='76' y1='0' x2='76' y2='152' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3c/defs%3e%3c/svg%3e";

function getWork({
  workEntityServiceOrigin,
  workRevisionId,
  bearerToken
}) {
  // eslint-disable-next-line max-len
  const wesUrl = `${workEntityServiceOrigin}/api/v1/works/revisions/${workRevisionId}?includeHidden=false&from=purcsReactClient`;
  return axios.get(wesUrl, {
    headers: {
      Authorization: `Bearer ${bearerToken}`
    }
  });
}

function getSideFromViewName(viewName) {
  if (!viewName) {
    return Side.ALL;
  }

  if (viewName.toLowerCase() === "front") {
    return Side.FRONT;
  }

  if (viewName.toLowerCase() === "back") {
    return Side.BACK;
  }

  throw new Error(`Cannot determine a Side value from viewName ${JSON.stringify(viewName)}`);
}
function getSideOrDefaultFromViewName(viewName) {
  try {
    return getSideFromViewName(viewName);
  } catch (ignored) {
    return Side.ALL;
  }
}

const defaultRenderingOptions = Object.freeze({
  renderingUrl: "https://rendering.mcp.cimpress.com/v1",
  tenant: "vp",
  category: "3d"
});
const defaultCameraOptions = Object.freeze({
  name: "orbital"
});
const defaultVortexOptions = Object.freeze({
  vortexUrl: "https://vortex.documents.cimpress.io/4.5.2b/vortex.js"
});

function useVortex(vortexUrl = defaultVortexOptions.vortexUrl) {
  const [vortex, setVortex] = useState(typeof window !== "undefined" && window.vortex);
  useEffect(() => {
    // If vortex is already on the window, use that
    if (vortex) {
      return;
    } // Otherwise, load the vortex script


    const loadVortexScript = () => {
      const script = document.createElement("script");
      script.src = vortexUrl;
      script.async = true;

      const onScriptLoad = () => {
        if (typeof window !== "undefined") {
          setVortex(window.vortex);
        }
      };

      const onScriptError = () => {
        setVortex(null);
        throw new Error("Error loading Vortex script");
      };

      script.addEventListener("load", onScriptLoad);
      script.addEventListener("error", onScriptError);
      document.body.appendChild(script);
      return () => {
        script.removeEventListener("load", onScriptLoad);
        script.removeEventListener("error", onScriptError);
      };
    }; // eslint-disable-next-line consistent-return


    return loadVortexScript();
  }, [vortexUrl, vortex]);
  return vortex;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function useInitializeVortexPreview({
  vortex,
  vortexContainerRef,
  SKU,
  selectedOptions,
  renderingInstructionsUrl
}) {
  const [vortexPreview, setVortexPreview] = useState(null);
  useEffect(() => {
    // Vortex not loaded yet
    if (!vortex || !vortexContainerRef || !vortexContainerRef.current) {
      return () => {};
    } // Create a new vortexPreview and initialize it


    if (!vortexPreview) {
      const newVortexPreview = new vortex.Preview();
      newVortexPreview.initialize({
        container: vortexContainerRef.current,
        camera: _extends({
          options: {
            productPadding: getProductPadding(SKU, selectedOptions)
          }
        }, defaultCameraOptions),
        productOptions: getVortexProductConfigurationFromSKU(vortex, SKU, selectedOptions),
        environment: {
          reflectionUrls: []
        },
        documentReference: {
          renderingInstructionsUrl: ensureProjectionsIgnored(renderingInstructionsUrl)
        },
        renderingOptions: _extends({}, defaultRenderingOptions),
        cacheBustInstructions: true
      }).then(() => {
        // Preview should already be initialized, but it's not,
        // so we busy-wait.  Lexie filed a ticket with Cimpress
        // Technology's Rendering team on 2019-12-18:
        // eslint-disable-next-line max-len
        // https://cimpress-support.atlassian.net/servicedesk/customer/portal/29/RS-8?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJxc2giOiJmZDI1MmUyNmUxZGMyMzgwMDFiMDE0M2UzN2E1YTU5M2I3NTM4NzI5Yjc3NTM4NmIzNDIyNTNhMjUxYmJmNzRjIiwiaXNzIjoic2VydmljZWRlc2stand0LXRva2VuLWlzc3VlciIsImNvbnRleHQiOnsidXNlciI6IjEzMDQ2IiwiaXNzdWUiOiJSUy04In0sImV4cCI6MTU3OTEyMDA5MiwiaWF0IjoxNTc2NzAwODkyfQ.-H39qWuhW3gA2itxowIVRXvXCUcBFh1D7VxjttKe3ck
        let attemptsRemaining = 50;
        const periodBetweenAttemptsMillisec = 100;

        const busyWaitPreviewInitialized = async () => {
          if (attemptsRemaining <= 0) {
            throw new Error("Timeout while trying to intialize Vortex preview");
          }

          if (!newVortexPreview.previewInitalized) {
            attemptsRemaining -= 1; // Wait until preview is initialized

            setTimeout(busyWaitPreviewInitialized, periodBetweenAttemptsMillisec);
            return;
          } // Now that the vortexPreview is fully initialized,
          // we can update the state.


          setVortexPreview(newVortexPreview); // Animate the vortex left and right slightly

          const animation = await newVortexPreview.animationManager.addAnimation("swivel", {
            damping: 0.3,
            range: 30
          });
          animation.start();
        };

        setTimeout(busyWaitPreviewInitialized, periodBetweenAttemptsMillisec);
      });
    }

    return () => {
      // Dispose of the vortexPreview
      // initialized is misssplled in vortex
      if (vortexPreview && vortexPreview.previewInitalized) {
        vortexPreview.dispose();
        setVortexPreview(null);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vortex, vortexContainerRef, vortexPreview]);
  return vortexPreview;
}

function useUpdateVortexPreview({
  vortex,
  vortexPreview,
  SKU,
  selectedOptions,
  renderingInstructionsUrl
}) {
  // Set the documentReference
  useEffect(() => {
    if (!vortexPreview || !vortexPreview.previewInitalized) {
      return;
    }

    vortexPreview.setDocumentReference({
      renderingInstructionsUrl
    });
  }, [vortexPreview, renderingInstructionsUrl]); // Set the Product Options

  useEffect(() => {
    if (!vortex || !vortexPreview || !vortexPreview.previewInitalized) {
      return;
    }

    vortexPreview.setProductOptions(getVortexProductConfigurationFromSKU(vortex, SKU, selectedOptions));
  }, [vortexPreview, vortex, SKU, selectedOptions]); // Update Vortex

  useEffect(() => {
    if (!vortex || !vortexPreview || !vortexPreview.previewInitalized) {
      return;
    }

    vortexPreview.update();
  }, [vortex, vortexPreview, SKU, selectedOptions, renderingInstructionsUrl]);
}

function useAnimateVortexPreview({
  vortexPreview,
  selectedSide
}) {
  useEffect(() => {
    if (!vortexPreview || !vortexPreview.previewInitalized || !selectedSide) {
      // We can't switch to the front or back until initialization
      // finishes.
      return;
    }

    if (selectedSide === Side.FRONT) {
      vortexPreview.lookAtFront();
    } else if (selectedSide === Side.BACK) {
      vortexPreview.lookAtBack();
    }
  }, [vortexPreview, selectedSide]);
}

function VortexPreview({
  SKU,
  selectedOptions,
  renderingInstructionsUrl,
  selectedSide
}) {
  // Each Vortex Previewer should have one instance of vortex, a container on the DOM
  // and a VortexPreview
  const vortex = useVortex();
  const vortexContainerRef = useRef(null);
  const vortexPreview = useInitializeVortexPreview({
    vortex,
    vortexContainerRef,
    SKU,
    selectedOptions,
    renderingInstructionsUrl
  });
  useUpdateVortexPreview({
    vortex,
    vortexPreview,
    SKU,
    selectedOptions,
    renderingInstructionsUrl: ensureProjectionsIgnored(renderingInstructionsUrl)
  });
  useAnimateVortexPreview({
    vortexPreview,
    selectedSide
  });
  return vortex ? /*#__PURE__*/React.createElement("div", {
    className: "vortex-container",
    ref: vortexContainerRef
  }) : /*#__PURE__*/React.createElement("div", {
    style: {
      height: "100%",
      display: "flex",
      alignItems: "center"
    }
  }, /*#__PURE__*/React.createElement(BasicPreloader, {
    accessibilityLabel: "Loading preview...",
    centered: true,
    size: "large"
  }));
}

var version = "0.0.1";

const messages = {
  errorCode: {
    id: "productPreview.errorHandler.errorCode",
    defaultMessage: "Error Code"
  }
};

function parseMessage(errObj) {
  try {
    return JSON.parse(errObj.message);
  } catch (e) {
    try {
      return JSON.parse(errObj);
    } catch (f) {
      return errObj;
    }
  }
}

function handleError(e) {
  const err = parseMessage(e);
  let errorCode = "12";

  if (err.errorCodeStack) {
    errorCode += `-${err.errorCodeStack}`;
  }

  let errorMessage = `${messages.errorCode.id}: ${errorCode} - `;

  if (err.moduleFunction) {
    errorMessage += err.moduleFunction;
  }

  if (err.response) {
    if (err.response.url) {
      errorMessage += `[${err.response.url}]`;
    }

    errorMessage += `[`;

    if (err.response.status) {
      errorMessage += `(${err.response.status})`;
    }

    if (err.response.statusText) {
      errorMessage += `${err.response.statusText}`;
    }

    errorMessage += `] `;
  }

  if (err.errorMessage) {
    errorMessage += ` ${err.errorMessage}`;
  }

  if (typeof err === "string") {
    errorMessage += ` ${err}`;
  }

  throw new Error(errorMessage);
}

const PurcsApi = {
  getDefaultView(origin, tenantId, culture, productKey, productVersion, optionSelections, requestor, purpose, previewInstructionsUri, width) {
    const params = {
      productVersion,
      requestor,
      optionSelections: JSON.stringify(optionSelections),
      purpose,
      previewInstructionsUri,
      "purcs-react-version": version
    };
    const qs = queryString.stringify(params); // eslint-disable-next-line max-len

    const baseUrl = `${origin}/api/v1/${tenantId}/${culture}/product/${productKey}/defaultViewImage?${qs}`;
    return {
      name: "default",
      src: `${baseUrl}&width=${width}`,
      srcSet: this.buildSrcSet(baseUrl, width)
    };
  },

  getAllViews(origin, tenantId, culture, productKey, productVersion, optionSelections, requestor, purpose, previewInstructionsUri, width, failHard = false) {
    const requestConfig = {
      params: {
        productVersion,
        requestor,
        optionSelections,
        purpose,
        previewInstructionsUri,
        width
      },
      headers: {
        "purcs-react-version": version
      },
      maxContentLength: 80000
    };
    return axios.get(`${origin}/api/v1/${tenantId}/${culture}/product/${productKey}/allViews`, requestConfig).then(purseResponse => {
      const purcsPreviews = purseResponse.data.views.map(view => {
        const parsedUrl = queryString.parseUrl(view._links.image.href);
        const parsedQuery = parsedUrl.query;
        delete parsedQuery.width;
        const baseUrl = `${parsedUrl.url}?${queryString.stringify(parsedQuery)}`;
        return {
          title: view._links.image.title,
          name: view.name,
          src: view._links.image.href,
          srcSet: this.buildSrcSet(baseUrl, width)
        };
      });
      return purcsPreviews;
    }).catch(error => {
      if (failHard) {
        throw error;
      } else {
        handleError(error); // eslint-disable-next-line no-console

        console.log(`Error retrieving all views: ${error.tostring()}`);
      }

      return [];
    });
  },

  buildSrcSet(baseUrl, width) {
    return [1, 2, 3].map(dpr => `${baseUrl}&width=${dpr * width} ${dpr}x`).join(", ");
  }

};

function PurcsPreview({
  coreProductKey,
  productVersion,
  purcsOrigin = "https://purcs.design.vpsvc.com",
  previewInstructionsUri,
  optionSelections = {},
  workRevisionId,
  bearerToken,
  previewMode = PreviewModes.Carousel,
  onClickMethod,
  viewName,
  fallbackPreviewTracking,
  requestor = defaultPurcsPreviewOptions.requestor,
  workEntityServiceOrigin = defaultPurcsPreviewOptions.workEntityServiceOrigin,
  tenantId = defaultPurcsPreviewOptions.tenantId,
  locale = defaultPurcsPreviewOptions.locale,
  purpose = defaultPurcsPreviewOptions.purpose,
  width = defaultPurcsPreviewOptions.width,
  pageSelectorOptions,
  fullSizeButtonOptions,
  maxHeight,
  accessibleTextForNext,
  accessibleTextForPrevious,
  afterChange,
  addOn
}) {
  const [merchandisingSelections, setMerchandisingSelections] = useState(optionSelections);
  const [renderingUrl, setRenderingUrl] = useState(previewInstructionsUri);
  const [purcsPreviews, setPurcsPreviews] = useState([]);
  const [coreProductId, setCoreProductId] = useState(coreProductKey);
  const [coreProductVersion, setProductVersion] = useState(productVersion);
  const [isProductUsableInVortex, setIsProductUsableInVortex] = useState(false);
  const [purcsImageDimensions, setPurcsImageDimensions] = useState([]);
  useEffect(() => {
    const getImageDimensions = async url => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });

        img.onerror = error => reject(error);

        img.src = url;
      });
    };

    const calculateAllPreviewDimensions = async () => {
      const unresolvedDimensions = purcsPreviews.map(async purcsPreview => getImageDimensions(purcsPreview.src));
      const previewDimensions = await Promise.all(unresolvedDimensions);
      setPurcsImageDimensions(previewDimensions);
    };

    calculateAllPreviewDimensions();
  }, [purcsPreviews, setPurcsImageDimensions]);
  useEffect(() => {
    let isStale = false;

    if (bearerToken && workRevisionId) {
      try {
        getWork({
          workEntityServiceOrigin,
          workRevisionId,
          bearerToken
        }).then(resp => {
          if (!isStale) {
            const work = resp.data;
            setRenderingUrl(work.design.displayUrl);

            if (work.product && work.product.key) {
              setCoreProductId(work.product.key);
            } else {
              var _work$design$metadata;

              setCoreProductId((_work$design$metadata = work.design.metadata) == null ? void 0 : _work$design$metadata.productKey);
            }

            if (work.product && work.product.version) {
              setProductVersion(work.product.version);
            }

            setIsProductUsableInVortex(isSKUSupported(work.design.metadata.productKey));
            setMerchandisingSelections(work.merchandising.merchandisingSelections);
          }
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(`Unable to get work revision ${workRevisionId}`, err);
      }
    }

    return () => {
      isStale = true;
    };
  }, [bearerToken, workEntityServiceOrigin, workRevisionId]);
  useEffect(() => {
    let isStale = false;

    if (coreProductId) {
      if (previewMode === PreviewModes.FirstPageOnly) {
        const purcsPreview = PurcsApi.getDefaultView(purcsOrigin, tenantId, locale, coreProductId, coreProductVersion, merchandisingSelections, requestor, purpose, renderingUrl, width);
        setPurcsPreviews([{
          src: purcsPreview.src,
          srcSet: purcsPreview.srcSet,
          name: "Product Preview"
        }]);
      } else if (previewMode === PreviewModes.Carousel || previewMode === PreviewModes.Vortex) {
        PurcsApi.getAllViews(purcsOrigin, tenantId, locale, coreProductId, coreProductVersion, merchandisingSelections, requestor, purpose, renderingUrl, width).then(previews => {
          if (!isStale) {
            setPurcsPreviews(previews);
          }
        });
      } else if (previewMode === PreviewModes.SpecificPageOnly) {
        PurcsApi.getAllViews(purcsOrigin, tenantId, locale, coreProductId, coreProductVersion, merchandisingSelections, requestor, purpose, renderingUrl, width).then(previews => {
          if (!isStale) {
            const specifiedPreview = previews.find(preview => preview.name.toLowerCase() === viewName.toLowerCase());

            if (specifiedPreview) {
              setPurcsPreviews([specifiedPreview]);
            } else {
              // eslint-disable-next-line no-console
              console.log(`purcs-react could not find a scene for ${viewName}`);
              setPurcsPreviews([previews[0]]);
            }
          }
        });
      }
    }

    return () => {
      isStale = true;
    };
  }, [locale, previewMode, purcsOrigin, purpose, requestor, tenantId, width, merchandisingSelections, viewName, renderingUrl, coreProductId, coreProductVersion]);
  const onClick = onClickMethod !== null ? onClickMethod : () => {};

  const onImageLoadError = evt => {
    // eslint-disable-next-line no-param-reassign
    evt.target.onerror = null; // eslint-disable-next-line no-param-reassign

    evt.target.src = img;
  };

  if (previewMode === PreviewModes.FirstPageOnly || previewMode === PreviewModes.SpecificPageOnly) {
    return /*#__PURE__*/React.createElement(Box, {
      bgc: purcsPreviews.length <= 0 ? "loading-shimmer" : undefined,
      className: classnames("purcs-preview single-image")
    }, purcsPreviews.map((preview, index) => {
      let maxWidth;

      if (maxHeight && purcsImageDimensions && purcsImageDimensions[index]) {
        maxWidth = getMaxWidthForMaxHeight(maxHeight, purcsImageDimensions[index].width, purcsImageDimensions[index].height);
      }

      return /*#__PURE__*/React.createElement("img", {
        className: classnames("purcs-preview-img", {
          clickable: onClick !== null
        }),
        src: preview.src,
        alt: preview.title,
        srcSet: preview.srcSet,
        loading: "lazy",
        key: preview.name,
        onClick: onClick,
        onError: onImageLoadError,
        style: {
          maxWidth
        }
      });
    }));
  }

  if (previewMode === PreviewModes.Vortex && isProductUsableInVortex && coreProductId) {
    const selectedSide = getSideOrDefaultFromViewName(viewName);
    return /*#__PURE__*/React.createElement(ErrorBoundary, {
      fallbackPreviewTracking: fallbackPreviewTracking
    }, /*#__PURE__*/React.createElement(VortexPreview, {
      SKU: coreProductId,
      selectedOptions: merchandisingSelections,
      renderingInstructionsUrl: renderingUrl,
      selectedSide: selectedSide
    }));
  }

  return /*#__PURE__*/React.createElement(CarouselPreview, {
    purcsPreviews: purcsPreviews,
    onClick: onClick,
    onImageLoadError: onImageLoadError,
    pageSelectorOptions: pageSelectorOptions,
    fullSizeButtonOptions: fullSizeButtonOptions,
    maxHeight: maxHeight,
    purcsImageDimensions: purcsImageDimensions,
    accessibleTextForPrevious: accessibleTextForPrevious,
    accessibleTextForNext: accessibleTextForNext,
    afterChange: afterChange,
    addOn: addOn
  });
}

export { PreviewModes, PurcsApi, PurcsPreview };
//# sourceMappingURL=index.js.map
